import type { TFunction } from 'i18next';
import api from '../../../services/api';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';

export interface OrderProductPayload {
  id?: string;
  productVariantId?: string | null;
  name: string;
  quantity: number;
  saleUnit?: QuantityUnit;
  saleQuantity?: number;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  unitPrice?: number;
  currency?: string;
  unit: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  description?: string;
  cnCode?: string;
  netWeight?: number;
  grossWeight?: number;
  countryOfOrigin?: string;
}

/**
 * An order with an approved sales invoice answers 409 with the shared
 * operational-exception contract (ORDER_CHANGED_AFTER_INVOICE, WP1 part 2).
 * Pass `confirmation` from a page that has `modal`/`t`; a cancelled
 * confirmation resolves to `false` and nothing was saved.
 */
export interface OrderSaveConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

export async function saveOrderProducts(
  orderId: string,
  products: OrderProductPayload[],
  currency?: string,
  confirmation?: OrderSaveConfirmation,
): Promise<boolean> {
  const payload: Record<string, unknown> = { products, currency };
  if (!confirmation) {
    await api.put(`/orders/${orderId}/products`, payload);
    return true;
  }
  const response = await submitWithOperationalException(
    (body) => api.put(`/orders/${orderId}/products`, body),
    payload,
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

export interface OrderFifoPreviewLine {
  id: string;
  purchaseUnitPrice: number | null;
  costPLN: number;
  marginPLN: number | null;
  marginPercent: number | null;
  hasShortage: boolean;
  averageCostPLN?: number | null;
}

export async function fetchOrderFifoPreview(orderId: string, products: unknown[], saved = false): Promise<OrderFifoPreviewLine[]> {
  const { data } = await api.post(`/orders/${orderId}/fifo-preview`, { products, saved });
  if (!Array.isArray(data) || data.some(line => !line || typeof line.id !== 'string'
    || !['costPLN'].every(key => typeof line[key] === 'number' && Number.isFinite(line[key]))
    || !['purchaseUnitPrice', 'marginPercent', 'marginPLN'].every(key => line[key] === null || (typeof line[key] === 'number' && Number.isFinite(line[key])))
    || (line.averageCostPLN !== undefined && line.averageCostPLN !== null && (typeof line.averageCostPLN !== 'number' || !Number.isFinite(line.averageCostPLN)))
    || typeof line.hasShortage !== 'boolean')) throw new Error('Invalid FIFO preview');
  return data;
}
